import React from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import Button from "../ui/Button";
import type { TermsReader } from "./useTermsReader";

interface TermsAcceptBarProps {
  reader: TermsReader;
}

/**
 * TermsAcceptBar — the read-gate status + "I accept" CTA, designed to sit in a
 * DialogFooter (docked, always visible) rather than below the scroll area.
 *
 * Renders the review-progress affordance (the "I've read it" escape hatch until
 * the user has scrolled to the bottom) and the primary accept button. The button
 * stays disabled until the document has been reviewed.
 */
const TermsAcceptBar: React.FC<TermsAcceptBarProps> = ({ reader }) => {
  const { t } = useTranslation();
  const { reviewed, submitting, data, markRead, accept } = reader;
  const acceptEnabled = reviewed && !submitting && !!data;

  return (
    <>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {reviewed ? (
          <>
            <CheckCircle2
              size={14}
              className="shrink-0 text-emerald-600 dark:text-emerald-400"
            />
            <span>{t("You've reviewed the document.")}</span>
          </>
        ) : (
          <button
            type="button"
            onClick={markRead}
            className="text-primary underline underline-offset-2 hover:opacity-80"
          >
            {t("I've read it")}
          </button>
        )}
      </div>
      <Button onClick={accept} loading={submitting} disabled={!acceptEnabled}>
        {t("I accept")}
      </Button>
    </>
  );
};

export default TermsAcceptBar;
